import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import fetcher from '../../../fetcher'
import { usePipelineStore } from '../stores/pipelineStore'

// Ceiling on how far into a column we'll page looking for the card. A stage with
// hundreds of opportunities shouldn't turn a deep link into an unbounded crawl;
// past this we say where it is rather than keep fetching.
const MAX_PAGES = 10

// How long to keep looking before giving up, and how often to re-check while a
// request the board itself started is still in flight.
const MAX_WAIT_MS = 8000
const POLL_MS = 150

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Handles `?opportunity=<id>` on the pipeline board — the deep link a
 * notification uses.
 *
 * Keeps the user *on the board*: it finds the card, pages that column in if the
 * card hasn't loaded yet, then highlights and scrolls to it. Earlier versions
 * redirected to the person's detail page, which contradicted the link's own
 * label ("Open on pipeline") and dropped the user somewhere they hadn't asked
 * to go.
 *
 * The API call is only to learn which stage the opportunity is in — columns are
 * paginated independently, so without the stage there is no way to know which
 * one to page.
 */
export const useOpportunityDeepLink = () => {
    const [searchParams] = useSearchParams()

    // Which id this mount already acted on.
    //
    // A ref rather than a cleanup-based cancel flag, and the distinction is
    // load-bearing: consuming the param changes `searchParams`, which is this
    // effect's own dependency, so React tears the effect down before the lookup
    // resolves. A cleanup guard would cancel every lookup it started.
    const handledRef = useRef(null)

    // Clear the highlight when the board unmounts, so returning to the pipeline
    // by any other route doesn't arrive with a card still ringed.
    useEffect(() => () => usePipelineStore.getState().setFocusedOpportunity(null), [])

    useEffect(() => {
        const rawId = searchParams.get('opportunity')
        if (!rawId || handledRef.current === rawId) return
        handledRef.current = rawId

        const opportunityId = Number(rawId)

        // The param is deliberately LEFT on the URL. Stripping it made the
        // address bar jump to /crm/pipeline, which reads as a failed redirect.
        // Keeping it makes the link shareable and re-focuses on refresh; the
        // ref above is what stops this running twice.

        const isLoaded = (stageId) =>
            (usePipelineStore.getState().opportunitiesForStage(stageId) || [])
                .some((opp) => opp.id === opportunityId)

        const run = async () => {
            let stageId
            try {
                const { data } = await fetcher.getCrmOpportunity(rawId)
                stageId = data?.stage
            } catch (e) {
                toast.error('That opportunity could not be found')
                return
            }

            if (!stageId) {
                // Won/lost opportunities leave the board entirely.
                toast.info('That opportunity is no longer on the pipeline')
                return
            }

            // Wait for the card to appear, paging the column in if it's deeper
            // than what's loaded.
            //
            // The waiting branch has to cover a column that exists but is still
            // FETCHING, not just a missing one. A fresh column is
            // `{rows: [], hasMore: true, loading: true}` while the board's own
            // first page is in flight, and `loadMoreForStage` is a no-op while
            // loading — so without the `loading` check this loop burned every
            // attempt in microseconds and gave up before a single card existed.
            const deadline = Date.now() + MAX_WAIT_MS
            let pagesLoaded = 0

            while (Date.now() < deadline) {
                const store = usePipelineStore.getState()
                if (isLoaded(stageId)) break

                const column = store.byStage[String(stageId)]
                if (!column || column.loading || column.loadingMore) {
                    await delay(POLL_MS)
                    continue
                }
                if (!column.hasMore || pagesLoaded >= MAX_PAGES) break

                pagesLoaded += 1
                await store.loadMoreForStage(stageId)
            }

            if (!isLoaded(stageId)) {
                toast.info('That opportunity is further down its column')
                return
            }

            // The card scrolls itself into view when it sees this (see
            // OpportunityCard) — it may still be mounting as we set it.
            //
            // The highlight persists rather than fading on a timer: on a board
            // of near-identical cards a highlight that has already timed out
            // leaves the user staring at the column wondering which one they
            // were sent to. It clears when the board unmounts.
            usePipelineStore.getState().setFocusedOpportunity(opportunityId)
        }

        run()
    }, [searchParams])
}
